import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Home", to: "/", sectionId: null },
  { label: "About", to: "/about", sectionId: null },
  { label: "Rooms", to: "/#rooms", sectionId: "rooms" },
  { label: "Booking", to: "/booking", sectionId: null },
  { label: "Contact", to: "/#contact", sectionId: "contact" },
];

const SECTIONS = ["home", "about", "rooms", "booking", "contact"];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const location = useLocation();

  // Determine active section based on scroll position (home page only)
  const updateActiveSection = useCallback(() => {
    if (location.pathname !== "/") return;

    const scrollY = window.scrollY + 80;

    // Check sections in reverse order to find the current one
    const sectionEls = SECTIONS.map((id) => ({
      id,
      el: document.getElementById(id),
    })).filter((s) => s.el);

    let current = "home";
    for (const { id, el } of sectionEls) {
      if (el.offsetTop <= scrollY) {
        current = id;
      }
    }
    setActiveSection(current);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      updateActiveSection();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    updateActiveSection();
    return () => window.removeEventListener("scroll", onScroll);
  }, [updateActiveSection]);

  // Reset active section on route change
  useEffect(() => {
    if (location.pathname === "/about") setActiveSection("about");
    else if (location.pathname === "/booking") setActiveSection("booking");
    else if (location.pathname === "/") setActiveSection("home");
    setMenuOpen(false);
  }, [location.pathname]);

  const isLinkActive = (link) => {
    if (link.to === "/about" && location.pathname === "/about") return true;
    if (link.to === "/booking" && location.pathname === "/booking") return true;
    if (link.to === "/" && location.pathname === "/" && activeSection === "home") return true;
    if (link.sectionId && location.pathname === "/" && activeSection === link.sectionId) return true;
    return false;
  };

  const handleAnchorClick = (e, link) => {
    if (link.to.startsWith("/#")) {
      e.preventDefault();
      const id = link.to.replace("/#", "");
      if (location.pathname !== "/") {
        window.location.href = link.to;
        return;
      }
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        setMenuOpen(false);
      }
    } else {
      setMenuOpen(false);
    }
  };

  const textColor = (active) => {
    if (active) return "text-brand";
    return scrolled ? "text-gray-700 hover:text-brand" : "text-white/90 hover:text-white";
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group" onClick={() => setMenuOpen(false)}>
          <div className="w-9 h-9 rounded-full bg-brand flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
              <path d="M3 17h2c.55 0 1-.45 1-1v-3h12v3c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1zm0 2h18v1H3v-1zM12 2C6.48 2 2 6.48 2 12h20C22 6.48 17.52 2 12 2z" />
            </svg>
          </div>
          <span
            className={`font-serif font-bold text-xl transition-colors duration-300 ${
              scrolled ? "text-gray-900" : "text-white"
            }`}
          >
            Mariner Lodge
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-7">
          {navLinks.map((link) => {
            const active = isLinkActive(link);
            return (
              <Link
                key={link.label}
                to={link.to}
                onClick={(e) => handleAnchorClick(e, link)}
                className={`relative font-medium text-sm transition-colors duration-300 group ${textColor(active)}`}
              >
                {link.label}
                {/* Active underline indicator */}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-brand rounded-full transition-all duration-300 ${
                    active ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            );
          })}
          <Link
            to="/booking"
            onClick={() => setMenuOpen(false)}
            className="ml-2 inline-flex items-center gap-1.5 bg-brand text-white font-semibold text-sm px-6 py-2.5 rounded-full shadow-lg hover:bg-indigo-700 transition-all duration-300 hover:shadow-indigo-300 hover:shadow-lg hover:-translate-y-0.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Book Now
          </Link>
        </nav>

        {/* Mobile hamburger */}
        <button
          className={`md:hidden p-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-brand/40 ${
            scrolled ? "text-gray-700 hover:bg-gray-100" : "text-white hover:bg-white/10"
          }`}
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          <svg className="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          menuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-white shadow-xl border-t border-gray-100 px-4 pt-2 pb-5">
          {navLinks.map((link) => {
            const active = isLinkActive(link);
            return (
              <Link
                key={link.label}
                to={link.to}
                onClick={(e) => handleAnchorClick(e, link)}
                className={`flex items-center gap-2 py-3 font-medium border-b border-gray-50 transition-colors duration-200 ${
                  active ? "text-brand" : "text-gray-700 hover:text-brand"
                }`}
              >
                {active && (
                  <span className="w-1.5 h-1.5 rounded-full bg-brand flex-shrink-0" />
                )}
                <span className={active ? "" : "ml-3.5"}>{link.label}</span>
              </Link>
            );
          })}
          <Link
            to="/booking"
            onClick={() => setMenuOpen(false)}
            className="mt-4 flex items-center justify-center gap-2 bg-brand text-white font-semibold text-sm px-6 py-3 rounded-full shadow-lg hover:bg-indigo-700 transition-all duration-300 w-full"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Book Now
          </Link>
        </div>
      </div>
    </header>
  );
}